// src/features/assessment/data/questions.ts

import { TrackId } from '../engine/types';

export interface QuizOption {
  id: string;
  text: string;
  tag?: string; // Used for internal logic/analytics
}

export interface QuizQuestion {
  id: string;
  text: string;
  options: QuizOption[];
}

export const TRACK_QUESTIONS: Record<TrackId, QuizQuestion[]> = {
  creator: [
    {
      id: 'q1_leverage',
      text: "You are at the start of your growth curve. Ideally, what does your team look like in 2 years?",
      options: [
        { id: 'a', text: "A bustling office. I expect to hire 50+ people to manage Sales/Ops manually.", tag: 'Traditionalist' },
        { id: 'b', text: "Lean and Lethal. I want to keep the core team under 12 people, but output like 50.", tag: 'Sovereign' },
        { id: 'c', text: "Just me. I want to remain a Solopreneur forever.", tag: 'Bootstrapper' }
      ]
    },
    {
      id: 'q2_infrastructure',
      text: "Since you don't have established operations yet, how do you want to build your infrastructure?",
      options: [
        { id: 'a', text: "We want to build our own proprietary internal tools from scratch using our own dev team.", tag: 'Builder_Mental' },
        { id: 'b', text: "We need custom workflows, but we are happy to build them on top of a proven AI foundation (Canvas OS).", tag: 'Sovereign' },
        { id: 'c', text: "We will just use standard SaaS tools (Salesforce, HubSpot) and figure it out manually.", tag: 'Renter' }
      ]
    },
    {
      id: 'q3_capital',
      text: "What is your priority for your runway and capital deployment?",
      options: [
        { id: 'a', text: "Minimizing Burn. We need to spend as little as possible to survive.", tag: 'Bootstrapper' },
        { id: 'b', text: "Maximizing Velocity. We need to ship faster than competitors at all costs.", tag: 'Velocity' },
        { id: 'c', text: "Building the Asset. We are willing to invest upfront to build an autonomous foundation.", tag: 'Sovereign' }
      ]
    }
  ],
  transformer: [
    {
      id: 'q1_signal',
      text: "Where do the critical decisions and handoffs happen in your team right now?",
      options: [
        { id: 'a', text: "Mostly offline. In the hallway, on phone calls, or via WhatsApp.", tag: 'Analog' },
        { id: 'b', text: "It's digital, but messy. We use Slack and Email, but info is scattered.", tag: 'Purgatory' },
        { id: 'c', text: "Structured. We live in tickets (Linear/Jira) and public channels.", tag: 'Architect' }
      ]
    },
    {
      id: 'q2_priority',
      text: "If autonomous agents could take over one function tomorrow, which would it be?",
      options: [
        { id: 'engineering', text: "Engineering & Product (QA, Docs, Triage)", tag: 'Track_Engineering' },
        { id: 'sales', text: "Sales & Support (Leads, CRM, Tickets)", tag: 'Track_Sales' },
        { id: 'marketing', text: "Marketing & Growth (Content, Ads, Research)", tag: 'Track_Marketing' },
        { id: 'ops', text: "Operations & HR (Onboarding, Finance, Admin)", tag: 'Track_Ops' }
      ]
    },
    {
      id: 'q3_memory',
      text: "If an Agent tried to learn your company policy by reading your Notion/Drive today, what would happen?",
      options: [
        { id: 'a', text: "It would learn nothing. We rely on 'Tribal Knowledge'.", tag: 'Analog' },
        { id: 'b', text: "It would get confused. Our docs are old and scattered.", tag: 'Purgatory' },
        { id: 'c', text: "It would learn the truth. We treat documentation as code.", tag: 'Architect' }
      ]
    },
    {
      id: 'q4_governance',
      text: "We treat AI Agents like Junior Employees. How do you view their evolution?",
      options: [
        { id: 'a', text: "Zero Tolerance. If it makes a mistake, we turn it off.", tag: 'Traditionalist' },
        { id: 'b', text: "Hands-on Coaching. We expect to micromanage the prompts.", tag: 'Tinkerer' },
        { id: 'c', text: "Managed Growth. We expect the Agent to learn over time via the Knowledge Graph.", tag: 'Asset_Builder' }
      ]
    }
  ],
  builder: [
    {
      id: 'q1_context',
      text: "What is the current state of the product?",
      options: [
        { id: 'existing', text: "It is an Existing SaaS/App (We have users/legacy code).", tag: 'Existing' },
        { id: 'new', text: "It is a New Product Concept (Starting from zero).", tag: 'New' }
      ]
    },
    // Dynamic Branching handled in Logic Hook
    {
      id: 'q2a_integration',
      text: "To make the AI useful, it needs to interact with your app. What is the most realistic path?",
      options: [
        { id: 'a', text: "Deep Backend Integration (Server-side logic).", tag: 'Backend_Native' },
        { id: 'b', text: "Frontend Hooks (The Shortcut / DOM Access).", tag: 'Frontend_Hook' },
        { id: 'c', text: "Read-Only (RAG / Q&A only).", tag: 'RAG_Only' }
      ]
    },
    {
      id: 'q3a_ux',
      text: "When the AI completes a task, how should the result look?",
      options: [
        { id: 'a', text: "A Chat Bubble (Text).", tag: 'Text' },
        { id: 'b', text: "Generative UI (Dynamic Charts/Buttons/Forms).", tag: 'GenUI' }
      ]
    },
    {
      id: 'q2b_core_value',
      text: "What is the core value proposition of your new app?",
      options: [
        { id: 'a', text: "The Interface (Better UI for ChatGPT).", tag: 'Wrapper' },
        { id: 'b', text: "The Workflow (AI-First logic like Perplexity/Cursor).", tag: 'AI_Native' }
      ]
    },
    {
      id: 'q3b_complexity',
      text: "Does your app require multi-step reasoning?",
      options: [
        { id: 'a', text: "No, it's mostly single-shot (Input -> Output).", tag: 'Simple' },
        { id: 'b', text: "Yes, it needs to think, plan, and self-correct.", tag: 'Agentic' }
      ]
    }
  ]
};

// Logic helper to show correct Builder questions
export const getQuestionsForTrack = (track: TrackId, answers: Record<string, string>): QuizQuestion[] => {
  const allQuestions = TRACK_QUESTIONS[track];
  
  if (track === 'builder') {
    const contextAnswer = answers['q1_context'];
    if (!contextAnswer) return [allQuestions[0]]; // Just first question
    
    const isNewApp = contextAnswer === 'new';
    
    if (isNewApp) {
      // Return New App Flow: Context -> Core Value -> Complexity
      return [allQuestions[0], allQuestions[3], allQuestions[4]];
    } else {
      // Return Existing App Flow: Context -> Integration -> UX
      return [allQuestions[0], allQuestions[1], allQuestions[2]];
    }
  }
  
  return allQuestions;
};